<template>
    <UForm
        :schema
        :state
        class="space-y-4"
        @submit="onSubmit"
        @error="onError"
    >
        <UFormField
            label="OŘ"
            name="MC_PRAHA"
        >
            <UInput
                v-model="state.MC_PRAHA"
                color="neutral"
                class="w-48"
                type="number"
            />
        </UFormField>
        <UFormField
            label="Okrsek"
            name="OKRSEK"
        >
            <UInput
                v-model="state.OKRSEK"
                color="neutral"
                class="w-48"
            />
        </UFormField>
        <UFormField
            label="Příjmení a jméno"
            name="OKRSKAR"
        >
            <UInput
                v-model="state.OKRSKAR"
                color="neutral"
                class="w-full"
            />
        </UFormField>
        <UFormField
            label="Telefon"
            name="TELEFON"
        >
            <UInput
                v-model="state.TELEFON"
                color="neutral"
                class="w-full"
            />
        </UFormField>
        <UFormField
            label="Volací znak"
            name="VOLACI_ZNAK"
        >
            <UInput
                v-model="state.VOLACI_ZNAK"
                color="neutral"
                class="w-64"
            />
        </UFormField>
        <UFormField
            label="Okrsková služebna"
            name="ADRESA_OKR"
        >
            <UInput
                v-model="state.ADRESA_OKR"
                color="neutral"
                class="w-64"
            />
        </UFormField>
        <UFormField
            label="Telefon služebna"
            name="TEL_USTREDNA"
        >
            <UInput
                v-model="state.TEL_USTREDNA"
                color="neutral"
                class="w-64"
            />
        </UFormField>
        <UFormField
            label="Úřední dny"
            name="DEN"
        >
            <UInput
                v-model="state.DEN"
                color="neutral"
                class="w-64"
            />
        </UFormField>
        <UFormField
            label="Úřední hodiny"
            name="HODINY"
        >
            <UInput
                v-model="state.HODINY"
                color="neutral"
                class="w-64"
            />
        </UFormField>
        <UFormField
            label="Obvodní ředitelství"
            name="OR_MP_PRAH"
        >
            <UInput
                v-model="state.OR_MP_PRAH"
                color="neutral"
                class="w-64"
            />
        </UFormField>
        <UFormField
            label="Telefon obvodní ředitelství"
            name="TEL_OR"
        >
            <UInput
                v-model="state.TEL_OR"
                color="neutral"
                class="w-64"
            />
        </UFormField>
        <UFormField
            label="Telefon úřad MČ"
            name="TELURAD"
        >
            <UInput
                v-model="state.TELURAD"
                color="neutral"
                class="w-64"
            />
        </UFormField>

        <USeparator class="py-4" />
        <div class="flex items-center gap-1.5 pb-4 sm:px-6 justify-end">
            <UButton
                label="Zrušit"
                color="neutral"
                variant="outline"
                class="font-promedium cursor-pointer"
                @click="emit('close')"
            />
            <UButton
                type="submit"
                label="Uložit"
                color="neutral"
                class="font-promedium cursor-pointer"
            />
        </div>
    </UForm>
</template>

<script lang="ts" setup>
import * as z from "zod"
import type { FormSubmitEvent, FormErrorEvent } from "@nuxt/ui"
const store = useMainStore()

const toast = useToast()

const { editItem } = defineProps<{
    editItem?: TableItem
}>()

const schema = z.object({
    MC_PRAHA: z
        .number({
            required_error: "OŘ je povinné",
        })
        .nonnegative("OŘ musí býť validní"),
    OKRSEK: z
        .string({
            required_error: "Okrsek je povinný",
        })
        .min(1, "Musí mít alespoň 1 znak")
        .max(9999),
    OKRSKAR: z
        .string({
            required_error: "Jméno je povinné",
        })
        .min(3, "Musí mít alespoň 3 znaky"),
    TELEFON: z.string().min(3, "Musí mít alespoň 3 znaky").optional(),
    VOLACI_ZNAK: z.string().min(3, "Musí mít alespoň 3 znaky").optional(),
    ADRESA_OKR: z
        .string({
            required_error: "Adresa okrskové služebny je povinná",
        })
        .min(3, "Musí mít alespoň 3 znaky"),
    TEL_USTREDNA: z.string().min(3, "Musí mít alespoň 3 znaky").optional(),
    DEN: z.string().min(3, "Musí mít alespoň 3 znaky").optional(),
    HODINY: z.string().min(3, "Musí mít alespoň 3 znaky").optional(),
    OR_MP_PRAH: z.string().min(3, "Musí mít alespoň 3 znaky").optional(),
    TEL_OR: z.string().min(3, "Musí mít alespoň 3 znaky").optional(),
    TELURAD: z.string().min(3, "Musí mít alespoň 3 znaky").optional(),
    EVIDEN_C: z.string().min(3, "Musí mít alespoň 3 znaky").optional(),
    OBJECTID: z.number().optional(),
})

type Schema = z.output<typeof schema>

const state = reactive<Partial<Schema>>({
    MC_PRAHA: editItem?.MC_PRAHA || undefined,
    OKRSEK: editItem?.OKRSEK || undefined,
    OKRSKAR: editItem?.OKRSKAR || undefined,
    TELEFON: editItem?.TELEFON || undefined,
    VOLACI_ZNAK: editItem?.VOLACI_ZNAK || undefined,
    ADRESA_OKR: editItem?.ADRESA_OKR || undefined,
    TEL_USTREDNA: editItem?.TEL_USTREDNA || undefined,
    DEN: editItem?.DEN || undefined,
    HODINY: editItem?.HODINY || undefined,
    OR_MP_PRAH: editItem?.OR_MP_PRAH || undefined,
    TEL_OR: editItem?.TEL_OR || undefined,
    TELURAD: editItem?.TELURAD || undefined,
    EVIDEN_C: editItem?.EVIDEN_C || undefined,
    OBJECTID: editItem?.OBJECTID || undefined,
})

async function onSubmit(event: FormSubmitEvent<Schema>) {
    console.log(event)
    let response: BackendResponse | undefined
    if (editItem) {
        const data = {
            ...state,
        }
        response = await store.updateSubmit(data)
    } else {
        const data = {
            ...state,
            EVIDEN_C: `${state.MC_PRAHA}_${state.OKRSEK}`,
        }
        response = await store.insertSubmit(data)
    }
    if (response.success === false) {
        toast.add({
            title: "Při ukládání došlo k chybě",
            description: response.message,
            color: "error",
            icon: "prime:times-circle",
        })
    } else {
        toast.add({
            title: "Úspěšně uloženo",
            color: "success",
            icon: "prime:check-circle",
        })
        emit("close")
    }
}

async function onError(event: FormErrorEvent) {
    if (event?.errors?.[0]?.id) {
        const element = document.getElementById(event.errors[0].id)
        element?.focus()
        element?.scrollIntoView({ behavior: "smooth", block: "center" })
    }
}

const emit = defineEmits(["close"])
</script>

<style scoped></style>
